import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dataService from '../services/dataService';
import { Request } from '../services/requestsService';
import authService from '../services/authService';
import TaskDetailModal from './TaskDetailModal';
import './TasksPage.css';

interface TasksPageProps {
  userId?: number;
}

const TasksPage: React.FC<TasksPageProps> = ({ userId }) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Request[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [selectedTask, setSelectedTask] = useState<Request | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchTasks();
  }, [userId]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let targetUserId = userId;
      
      if (!targetUserId) {
        // Сначала пробуем получить ID из localStorage (сохраненный при логине)
        const savedUserId = authService.getUserId();
        if (savedUserId) {
          console.log('Using saved user ID:', savedUserId);
          targetUserId = savedUserId;
        } else {
          // Пытаемся получить ID пользователя разными способами
          try {
            // Сначала пробуем новый эндпоинт
            const profile = await authService.getMe();
            console.log('getMe success:', profile);
            targetUserId = Number(profile.id);
            setCurrentUser(profile);
          } catch (meError) {
            console.log('getMe failed, trying getProfile:', meError);
            try {
              // Если не работает, пробуем старый эндпоинт
              const profile = await authService.getProfile();
              console.log('getProfile success:', profile);
              targetUserId = Number(profile.id);
              setCurrentUser(profile);
            } catch (profileError) {
              console.log('getProfile failed, trying email from token:', profileError);
              // Если и это не работает, пробуем получить email из токена и найти пользователя
              const email = authService.getEmailFromToken();
              if (email) {
                console.log('Using email from token:', email);
                try {
                  const user = await authService.getUserByEmail(email);
                  if (user) {
                    targetUserId = Number(user.id);
                    setCurrentUser(user);
                    console.log('Found user by email:', user);
                  } else {
                    throw new Error('User not found by email');
                  }
                } catch (emailError) {
                  console.log('Failed to get user by email:', emailError);
                  // Последняя попытка - получить ID из токена
                  const userIdFromToken = authService.getUserIdFromToken();
                  if (userIdFromToken) {
                    targetUserId = userIdFromToken;
                    console.log('Using user ID from token:', targetUserId);
                  } else {
                    throw new Error('Could not get user ID from any source');
                  }
                }
              } else {
                throw new Error('No email found in token');
              }
            }
          }
        }
      }
      
      console.log('Target user ID:', targetUserId);
      
      const tasksResponse = await dataService.getRequestsByResponsible(targetUserId);
      console.log('Tasks response:', tasksResponse);
      
      // Теперь dataService возвращает правильный формат с content
      setTasks(tasksResponse.content || []);
    } catch (err) {
      console.error('Error fetching user info:', err);
      setError('Ошибка при загрузке информации о пользователе');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU');
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return '#ef4444';
      case 'MEDIUM':
        return '#f59e0b';
      case 'LOW':
        return '#10b981';
      default:
        return '#6b7280';
    }
  };

  const getPriorityText = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'Высокий';
      case 'MEDIUM':
        return 'Средний';
      case 'LOW':
        return 'Низкий';
      default:
        return priority;
    }
  };

  const getStatusColor = (status: any) => {
    return status?.color || '#6b7280';
  };

  const handleOpenTaskDetail = (task: Request) => {
    navigate(`/task/${task.id}`);
  };

  const handleCloseModal = () => {
    // Убираем старые функции модального окна
  };

  const handleModalSuccess = () => {
    // Убираем старые функции модального окна
  };

  if (loading) {
    return (
      <div className="tasks-page">
        <div className="tasks-header">
          <h1>Мои задачи</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Загрузка задач...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tasks-page">
        <div className="tasks-header">
          <h1>Мои задачи</h1>
        </div>
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchTasks} className="retry-button">
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tasks-page">
      <div className="tasks-header">
        <h1>Мои задачи</h1>
        {currentUser && (
          <p className="user-info">
            {currentUser.name} {currentUser.surname} ({currentUser.position})
          </p>
        )}
      </div>

      <div className="tasks-stats">
        <div className="stat-card">
          <div className="stat-number">{tasks?.length || 0}</div>
          <div className="stat-label">Всего задач</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {tasks?.filter(task => task.status?.name === 'В работе').length || 0}
          </div>
          <div className="stat-label">В работе</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {tasks?.filter(task => task.priority === 'HIGH').length || 0}
          </div>
          <div className="stat-label">Высокий приоритет</div>
        </div>
      </div>

      <div className="tasks-list">
        {!tasks || tasks.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>Нет задач</h3>
            <p>У вас пока нет назначенных задач</p>
          </div>
        ) : (
          tasks.map((task) => (
            <div 
              key={task.id} 
              className="task-card clickable"
              onClick={() => handleOpenTaskDetail(task)}
            >
              <div className="task-header">
                <div className="task-title">{task.title}</div>
                <div className="task-priority">
                  <span 
                    className="priority-badge"
                    style={{ backgroundColor: getPriorityColor(task.priority) }}
                  >
                    {getPriorityText(task.priority)}
                  </span>
                </div>
              </div>

              <div className="task-description">
                {task.description}
              </div>

              <div className="task-details">
                <div className="detail-row">
                  <div className="detail-item">
                    <span className="detail-label">Рейс:</span>
                    <span className="detail-value">
                      {task.flight?.flightNumber} ({task.flight?.departureAirport} → {task.flight?.arrivalAirport})
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Машина:</span>
                    <span className="detail-value">
                      {task.car?.name} {task.car?.model}
                    </span>
                  </div>
                </div>

                <div className="detail-row">
                  <div className="detail-item">
                    <span className="detail-label">Статус:</span>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(task.status) }}
                    >
                      {task.status?.name}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Части:</span>
                    <span className="detail-value">
                      {task.aircraftParts && task.aircraftParts.length > 0 
                        ? task.aircraftParts.map(part => part.aircraftPartName).join(', ')
                        : 'Не указаны'
                      }
                    </span>
                  </div>
                </div>

                <div className="detail-row">
                  <div className="detail-item">
                    <span className="detail-label">Создано:</span>
                    <span className="detail-value">{formatDateTime(task.createdAt)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Создатель:</span>
                    <span className="detail-value">
                      {task.creator?.name} {task.creator?.surname}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {selectedTask && (
        <TaskDetailModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          task={selectedTask}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default TasksPage; 